import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Phone,
  MapPin,
  Star,
  Clock,
  Shield,
  Truck,
  Home,
  Building2,
  Trash2,
  Sofa,
  HardHat,
  Package,
  ChevronRight,
  CheckCircle,
  Menu,
  X,
  ArrowRight,
  Facebook,
  Mail,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import ServicePage from "./pages/ServicePage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trash-haulers-tx/artifacts/52ejxn9j_dallas%20haul%20stars.jpg";
const PHONE_NUMBER = "972-330-3678";
const FACEBOOK_URL = "https://www.facebook.com/p/Dallas-Haul-Stars-LLC-61574536672374/";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true }
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "Why Us", href: "#why-us" },
    { name: "How It Works", href: "#process" },
    { name: "Reviews", href: "#reviews" },
    { name: "Service Areas", href: "#areas" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Dallas Haul Stars Logo" 
              className="h-14 w-auto rounded-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-slate-700 hover:text-brand-primary font-dm font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Phone CTA - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center gap-2 text-brand-primary font-dm font-bold text-lg hover:text-brand-navy transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-5 h-5" />
              {PHONE_NUMBER}
            </a>
            <Button
              asChild
              className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold px-6 py-3 rounded-md transition-all duration-200 hover:scale-105"
              data-testid="nav-cta-btn"
            >
              <a href="#contact">Get Free Quote</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden py-4 border-t border-gray-100"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-slate-700 hover:text-brand-primary font-dm font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="flex items-center gap-2 text-brand-primary font-dm font-bold text-lg py-2"
              >
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </a>
              <Button
                asChild
                className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold w-full"
              >
                <a href="#contact">Get Free Quote</a>
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen pt-20 bg-gradient-to-br from-brand-navy via-slate-900 to-brand-navy overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-brand-primary/20 border border-brand-primary/40 rounded-full px-4 py-2 mb-6"
            >
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-dm font-medium">5-Star Rated in Dallas</span>
            </motion.div>

            <h1 className="font-barlow font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight uppercase mb-6">
              Dallas&apos;s Premier
              <span className="block text-brand-primary">Junk Removal</span>
              & Haul Service
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 font-dm mb-8 max-w-xl">
              Fast, affordable, and eco-friendly junk removal for homes and businesses across the Dallas Metro. 
              <span className="text-white font-semibold"> You point, we haul.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                asChild
                size="lg"
                className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg px-8 py-6 rounded-md transition-all duration-200 hover:scale-105 shadow-lg shadow-orange-500/30"
                data-testid="hero-cta-primary"
              >
                <a href="#contact" className="flex items-center gap-2">
                  Get Your Free Quote
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-brand-navy font-dm font-bold text-lg px-8 py-6 rounded-md transition-all duration-200"
                data-testid="hero-cta-phone"
              >
                <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call {PHONE_NUMBER}
                </a>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-300 font-dm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Free Estimates</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Fully Insured</span>
              </div>
            </div>
          </motion.div>

          {/* Right - Image/Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1684426524165-3d80f3277aea?w=800&auto=format&fit=crop&q=80"
                alt="Junk removal truck loaded with debris"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent"></div>
              
              {/* Floating Badge */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-barlow font-bold text-brand-navy text-lg">Book Online & Save!</p>
                    <p className="text-slate-600 text-sm font-dm">Get an instant quote in minutes</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F8FAFC"/>
        </svg>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Home,
      title: "Residential Junk Removal",
      slug: "residential-junk-removal",
      description: "Clear out garages, attics, basements, and homes. We handle everything from single items to full house cleanouts.",
      features: ["Single Item Pickup", "Full House Cleanouts", "Estate Cleanouts"]
    },
    {
      icon: Building2,
      title: "Commercial Junk Removal",
      slug: "commercial-junk-removal",
      description: "Office cleanouts, retail spaces, and warehouses. Keep your business clutter-free and productive.",
      features: ["Office Furniture", "Equipment Disposal", "Warehouse Cleanouts"]
    },
    {
      icon: Sofa,
      title: "Furniture Removal",
      slug: "furniture-removal",
      description: "Old couches, mattresses, desks, and more. We do the heavy lifting so you don't have to.",
      features: ["Sofas & Couches", "Mattresses & Beds", "Tables & Chairs"]
    },
    {
      icon: Package,
      title: "Appliance Removal",
      slug: "appliance-removal",
      description: "Refrigerators, washers, dryers, and all major appliances responsibly disposed of.",
      features: ["Refrigerators", "Washers & Dryers", "Water Heaters"]
    },
    {
      icon: HardHat,
      title: "Construction Debris",
      slug: "construction-debris-removal",
      description: "Post-renovation cleanup, drywall, lumber, and construction materials hauled away fast.",
      features: ["Renovation Debris", "Drywall & Lumber", "Contractor Cleanups"]
    },
    {
      icon: Trash2,
      title: "Estate Cleanouts",
      slug: "estate-cleanout",
      description: "Compassionate and thorough estate clearing services. We handle everything with care and respect.",
      features: ["Full Property Clearing", "Donation Coordination", "Same-Day Service"]
    },
  ];

  return (
    <section id="services" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <span className="inline-block bg-brand-primary/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4">
            Our Services
          </span>
          <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-navy uppercase tracking-tight mb-4">
            What We Haul Away
          </h2>
          <p className="text-slate-600 font-dm text-lg max-w-2xl mx-auto">
            From a single item to a full property cleanout, we handle junk removal of any size across Dallas.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <Link
              key={service.title}
              to={`/services/${service.slug}`}
              className="block"
              data-testid={`service-card-${index}`}
            >
              <motion.div
                variants={fadeInUp}
                whileHover={{ y: -8, boxShadow: "0 20px 40px -12px rgba(30, 111, 186, 0.15)" }}
                className="bg-white rounded-xl p-8 border border-slate-200 transition-all duration-300 group h-full"
              >
                <div className="w-14 h-14 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-brand-primary group-hover:scale-110 transition-all duration-300">
                  <service.icon className="w-7 h-7 text-brand-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-barlow font-bold text-xl text-brand-navy mb-3">{service.title}</h3>
                <p className="text-slate-600 font-dm mb-4">{service.description}</p>
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-500 font-dm">
                      <CheckCircle className="w-4 h-4 text-brand-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <span className="text-brand-primary font-dm font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ChevronRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>
          ))}
        </motion.div>

        <motion.div
          {...fadeInUp}
          className="text-center mt-12"
        >
          <Button
            asChild
            size="lg"
            className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg px-10 py-6 rounded-md transition-all duration-200 hover:scale-105"
            data-testid="services-cta"
          >
            <a href="#contact" className="flex items-center gap-2">
              Get a Free Quote Now
              <ChevronRight className="w-5 h-5" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const benefits = [
    {
      icon: Clock,
      title: "Same-Day Service",
      description: "Need it gone today? We offer fast, same-day junk removal to fit your schedule."
    },
    {
      icon: Shield,
      title: "Fully Licensed & Insured",
      description: "Your property is protected. We're fully insured for your peace of mind."
    },
    {
      icon: Star,
      title: "5-Star Rated",
      description: "Join hundreds of satisfied Dallas customers who trust us for their junk removal."
    },
    {
      icon: Truck,
      title: "Eco-Friendly Disposal",
      description: "We recycle and donate whenever possible. Good for you, good for the planet."
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            {...fadeInUp}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1730376319010-4ce5b8b2e385?w=800&auto=format&fit=crop&q=80"
                alt="Professional team loading truck with items"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-navy/80 to-transparent p-8">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-brand-primary border-2 border-white flex items-center justify-center text-white text-sm font-bold">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                  </div>
                  <div className="text-white">
                    <p className="font-barlow font-bold text-lg">Trusted by 500+ Dallas Residents</p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      ))}
                      <span className="text-sm ml-2">5.0 Rating</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            {...fadeInUp}
          >
            <span className="inline-block bg-brand-primary/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4">
              Why Dallas Chooses Us
            </span>
            <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-navy uppercase tracking-tight mb-6">
              The Haul Stars Difference
            </h2>
            <p className="text-slate-600 font-dm text-lg mb-10">
              We&apos;re not just another junk removal company. We&apos;re your neighbors, committed to keeping Dallas clean and clutter-free with professional, courteous service.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`benefit-${index}`}
                >
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-barlow font-bold text-lg text-brand-navy mb-1">{benefit.title}</h3>
                    <p className="text-slate-600 font-dm text-sm">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Book Your Appointment",
      description: "Call us or fill out our online form. Pick a time that works for you - we offer same-day and next-day appointments."
    },
    {
      number: "02",
      title: "Get Your Free Quote",
      description: "Our team arrives on time, assesses your junk, and provides a transparent, no-obligation price on the spot."
    },
    {
      number: "03",
      title: "We Haul It Away",
      description: "Approve the quote and we get to work immediately. We handle all the heavy lifting and leave your space clean."
    }
  ];

  return (
    <section id="process" className="py-24 bg-brand-navy relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2.5a2.5 2.5 0 015 0V18h15v2H25v2.5a2.5 2.5 0 01-5 0z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <span className="inline-block bg-white/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4 border border-brand-primary/30">
            Simple Process
          </span>
          <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
            How It Works
          </h2>
          <p className="text-slate-300 font-dm text-lg max-w-2xl mx-auto">
            Getting rid of your junk has never been easier. Three simple steps and your space is clear.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative"
              data-testid={`process-step-${index}`}
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[60%] w-full h-0.5 bg-brand-primary/30" />
              )}
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 relative z-10 hover:bg-white/10 transition-all duration-300">
                <span className="font-barlow font-extrabold text-6xl text-brand-primary/30 absolute top-4 right-6">
                  {step.number}
                </span>
                <div className="w-16 h-16 bg-brand-primary rounded-xl flex items-center justify-center mb-6">
                  <span className="font-barlow font-bold text-2xl text-white">{step.number}</span>
                </div>
                <h3 className="font-barlow font-bold text-xl text-white mb-3">{step.title}</h3>
                <p className="text-slate-300 font-dm">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...fadeInUp}
          className="text-center mt-12"
        >
          <Button
            asChild
            size="lg"
            className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg px-10 py-6 rounded-md transition-all duration-200 hover:scale-105"
            data-testid="process-cta"
          >
            <a href="#contact" className="flex items-center gap-2">
              Start Your Free Quote
              <ArrowRight className="w-5 h-5" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      location: "Dallas, TX",
      rating: 5,
      text: "Amazing service! They showed up on time, gave me a fair quote, and had my garage cleared out in no time. Highly recommend Dallas Haul Stars!",
      service: "Garage Cleanout"
    },
    {
      name: "Michael R.",
      location: "Plano, TX",
      rating: 5,
      text: "Professional, courteous, and efficient. They helped us with an estate cleanout and made a difficult time so much easier. Thank you!",
      service: "Estate Cleanout"
    },
    {
      name: "Jennifer T.",
      location: "Irving, TX",
      rating: 5,
      text: "Best junk removal service in Dallas! They recycled what they could and disposed of everything responsibly. Will definitely use again.",
      service: "Home Cleanout"
    }
  ];

  return (
    <section id="reviews" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          {...fadeInUp}
          className="text-center mb-16"
        >
          <span className="inline-block bg-brand-primary/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4">
            Customer Reviews
          </span>
          <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-navy uppercase tracking-tight mb-4">
            What Dallas Says About Us
          </h2>
          <p className="text-slate-600 font-dm text-lg max-w-2xl mx-auto">
            Don&apos;t just take our word for it. See what our satisfied customers have to say.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-slate-700 font-dm mb-6 leading-relaxed">&ldquo;{testimonial.text}&rdquo;</p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div>
                  <p className="font-barlow font-bold text-brand-navy">{testimonial.name}</p>
                  <p className="text-sm text-slate-500 font-dm">{testimonial.location}</p>
                </div>
                <span className="text-xs bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-full font-dm">
                  {testimonial.service}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Google/Facebook Review Link */}
        <motion.div
          {...fadeInUp}
          className="text-center mt-12"
        >
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-navy font-dm font-semibold transition-colors"
            data-testid="reviews-link"
          >
            <Facebook className="w-5 h-5" />
            See More Reviews on Facebook
            <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Service Areas Section
const ServiceAreasSection = () => {
  const areas = [
    "Dallas", "Plano", "Irving", "Garland", "Richardson", "Carrollton",
    "Mesquite", "Grand Prairie", "Arlington", "Frisco", "McKinney", "Allen",
    "Lewisville", "Flower Mound", "Denton", "The Colony", "Addison", "Farmers Branch"
  ];

  return (
    <section id="areas" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            {...fadeInUp}
          >
            <span className="inline-block bg-brand-primary/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4">
              Service Coverage
            </span>
            <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-navy uppercase tracking-tight mb-6">
              Serving All of Dallas Metro
            </h2>
            <p className="text-slate-600 font-dm text-lg mb-8">
              From downtown Dallas to the surrounding suburbs, Dallas Haul Stars is your local junk removal solution. We proudly serve the entire DFW metroplex.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {areas.map((area) => (
                <span
                  key={area}
                  className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-dm hover:bg-brand-primary hover:text-white transition-colors cursor-default"
                >
                  {area}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 p-4 bg-brand-primary/5 rounded-xl">
              <MapPin className="w-8 h-8 text-brand-primary" />
              <div>
                <p className="font-barlow font-bold text-brand-navy">Don&apos;t see your area?</p>
                <p className="text-slate-600 font-dm text-sm">Call us! We likely serve your neighborhood too.</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Image */}
          <motion.div
            {...fadeInUp}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1563219125-60d10ffe8877?w=800&auto=format&fit=crop&q=80"
                alt="Dallas Texas skyline"
                className="w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                  <p className="font-barlow font-bold text-brand-navy text-xl mb-2">Dallas, Texas</p>
                  <p className="text-slate-600 font-dm text-sm">Proudly serving the Dallas-Fort Worth metroplex and surrounding communities since day one.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact/Quote Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (HubSpot integration placeholder)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Quote request submitted! We'll contact you shortly.", {
      description: "Expect a call within 1 hour during business hours.",
    });
    
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      service: "",
      message: ""
    });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-brand-navy relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Content */}
          <motion.div
            {...fadeInUp}
            className="text-white"
          >
            <span className="inline-block bg-white/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4 border border-brand-primary/30">
              Get Your Free Quote
            </span>
            <h2 className="font-barlow font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-6">
              Ready to Clear Your Space?
            </h2>
            <p className="text-slate-300 font-dm text-lg mb-10">
              Fill out the form and we&apos;ll get back to you within the hour with a free, no-obligation quote. Or call us directly for immediate service.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4">
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="flex items-center gap-4 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl p-5 transition-all duration-300 group"
                data-testid="contact-phone-card"
              >
                <div className="w-14 h-14 bg-brand-accent rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 font-dm text-sm">Call Us Now</p>
                  <p className="font-barlow font-bold text-xl">{PHONE_NUMBER}</p>
                </div>
              </a>

              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl p-5 transition-all duration-300 group"
                data-testid="contact-facebook-card"
              >
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Facebook className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-slate-400 font-dm text-sm">Message Us</p>
                  <p className="font-barlow font-bold text-xl">Facebook Page</p>
                </div>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-slate-400 font-dm text-sm mb-4">Why customers trust us:</p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-sm font-dm">Fully Insured</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg">
                  <Clock className="w-5 h-5 text-green-400" />
                  <span className="text-sm font-dm">Same-Day Service</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm font-dm">5-Star Rated</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-2xl"
          >
            {/* HubSpot Placeholder Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm font-dm">
                <strong>Note:</strong> This form will be connected to HubSpot for lead management. Currently using a placeholder submission.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="w-full"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(972) 555-0123"
                    required
                    className="w-full"
                    data-testid="form-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full"
                  data-testid="form-email"
                />
              </div>

              <div>
                <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                  Service Address *
                </label>
                <Input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St, Dallas, TX"
                  required
                  className="w-full"
                  data-testid="form-address"
                />
              </div>

              <div>
                <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                  Service Needed *
                </label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}
                >
                  <SelectTrigger className="w-full" data-testid="form-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential Junk Removal</SelectItem>
                    <SelectItem value="commercial">Commercial Junk Removal</SelectItem>
                    <SelectItem value="furniture">Furniture Removal</SelectItem>
                    <SelectItem value="appliance">Appliance Removal</SelectItem>
                    <SelectItem value="construction">Construction Debris</SelectItem>
                    <SelectItem value="estate">Estate Cleanout</SelectItem>
                    <SelectItem value="other">Other / Not Sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-dm font-medium text-slate-700 mb-2">
                  Tell Us About Your Junk
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe what you need removed (e.g., old couch, garage full of items, construction debris...)"
                  rows={4}
                  className="w-full"
                  data-testid="form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg py-6 rounded-md transition-all duration-200 hover:scale-[1.02]"
                data-testid="form-submit"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Get My Free Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-slate-500 text-sm font-dm">
                We respond within 1 hour during business hours
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Dallas Haul Stars Logo" 
              className="h-20 w-auto rounded-lg mb-6"
            />
            <p className="text-slate-400 font-dm mb-6 max-w-md">
              Dallas Haul Stars LLC is your trusted local junk removal and hauling service. We serve the entire Dallas-Fort Worth metroplex with fast, affordable, and eco-friendly service.
            </p>
            <div className="flex items-center gap-4">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-brand-primary rounded-lg flex items-center justify-center transition-colors"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="w-10 h-10 bg-white/10 hover:bg-brand-primary rounded-lg flex items-center justify-center transition-colors"
                data-testid="footer-phone"
                aria-label="Call us"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-barlow font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3 font-dm">
              <li><a href="#services" className="text-slate-400 hover:text-white transition-colors">Our Services</a></li>
              <li><a href="#why-us" className="text-slate-400 hover:text-white transition-colors">Why Choose Us</a></li>
              <li><a href="#process" className="text-slate-400 hover:text-white transition-colors">How It Works</a></li>
              <li><a href="#reviews" className="text-slate-400 hover:text-white transition-colors">Reviews</a></li>
              <li><a href="#areas" className="text-slate-400 hover:text-white transition-colors">Service Areas</a></li>
              <li><a href="#contact" className="text-slate-400 hover:text-white transition-colors">Get a Quote</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-barlow font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4 font-dm">
              <li>
                <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-brand-primary" />
                  {PHONE_NUMBER}
                </a>
              </li>
              <li>
                <a href="mailto:info@dallashaulstars.com" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-brand-primary" />
                  info@dallashaulstars.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-brand-primary flex-shrink-0 mt-1" />
                <span>Serving Dallas, TX<br />& Surrounding Areas</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-brand-primary" />
                <span>Mon-Sat: 7AM-7PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm font-dm text-center md:text-left">
            &copy; {new Date().getFullYear()} Dallas Haul Stars LLC. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm font-dm">
            Dallas&apos;s Premier Junk Removal Service
          </p>
        </div>
      </div>
    </footer>
  );
};

// Homepage Component
const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>Dallas Junk Removal | Same-Day Service | Dallas Haul Stars LLC</title>
        <meta name="description" content="Dallas Haul Stars LLC offers professional junk removal services in Dallas-Fort Worth. Residential, commercial, furniture, appliance removal. Same-day service. Call 972-330-3678." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dallashaulstars.com" />
        <meta property="og:title" content="Dallas Junk Removal | Same-Day Service | Dallas Haul Stars LLC" />
        <meta property="og:description" content="Professional junk removal services in Dallas-Fort Worth. Residential, commercial, furniture, appliance removal. Same-day service available." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dallashaulstars.com" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_trash-haulers-tx/artifacts/52ejxn9j_dallas%20haul%20stars.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dallas Haul Stars LLC",
            "image": "https://customer-assets.emergentagent.com/job_trash-haulers-tx/artifacts/52ejxn9j_dallas%20haul%20stars.jpg",
            "telephone": "972-330-3678",
            "url": "https://dallashaulstars.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Dallas",
              "addressRegion": "TX",
              "postalCode": "75201",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "32.7767",
              "longitude": "-96.7970"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "07:00",
              "closes": "19:00"
            },
            "priceRange": "$$",
            "sameAs": ["https://www.facebook.com/p/Dallas-Haul-Stars-LLC-61574536672374/"]
          })}
        </script>
      </Helmet>
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <WhyChooseUsSection />
      <ProcessSection />
      <TestimonialsSection />
      <ServiceAreasSection />
      <ContactSection />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        richColors 
        closeButton
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services/:slug" element={<ServicePage />} />
      </Routes>
    </div>
  );
}

export default App;
